import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Progress,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import '@mantine/dropzone/styles.css';
import { DataTable } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { Link } from 'react-router-dom';
import {
  IconArrowUp,
  IconDownload,
  IconFileUpload,
  IconHistory,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import {
  useDocumentsList,
  useFinalizeDocument,
  useRequestDownloadUrl,
  useRequestUploadUrl,
  useSoftDeleteDocument,
} from '../../queries/documents';
import { MAX_UPLOAD_SIZE_BYTES, putToR2, sha256Hex } from '../../lib/upload';
import { ApiError } from '../../lib/api-error';
import { useProjectStore } from '../../project/project-store';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { humanSize } from './human-size';
import type { Document } from '../../types/api';
import dayjs from 'dayjs';

const ACCEPT = [MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg, 'image/tiff', 'image/webp'];
const MAX_SIZE = MAX_UPLOAD_SIZE_BYTES;

interface UploadTaskState {
  id: string;
  name: string;
  total: number;
  phase: 'hashing' | 'presigning' | 'uploading' | 'finalizing' | 'done' | 'error';
  loaded: number;
  error?: string;
}

export function DocumentsPage() {
  const projectId = useProjectStore((s) => s.projectId);
  const projectRole = useProjectStore((s) => s.projectRole);
  const canWrite = projectRole === 'owner' || projectRole === 'moderator' || projectRole === 'editor';

  const [q, setQ] = useState('');
  const { data: docs = [], isLoading } = useDocumentsList({ q: q.trim() || undefined });

  const requestUrl = useRequestUploadUrl();
  const finalize = useFinalizeDocument();
  const requestDownload = useRequestDownloadUrl();
  const softDelete = useSoftDeleteDocument();

  const [tasks, setTasks] = useState<UploadTaskState[]>([]);
  const activeTasks = useMemo(() => tasks.filter((t) => t.phase !== 'done'), [tasks]);
  const [previewDoc, setPreviewDoc] = useState<Document | null>(null);

  async function handleDrop(files: File[]) {
    for (const file of files) {
      const localId = crypto.randomUUID();
      setTasks((prev) => [
        ...prev,
        { id: localId, name: file.name, total: file.size, phase: 'hashing', loaded: 0 },
      ]);
      void uploadOne(file, localId);
    }
  }

  async function uploadOne(file: File, localId: string) {
    const update = (patch: Partial<UploadTaskState>) =>
      setTasks((prev) => prev.map((t) => (t.id === localId ? { ...t, ...patch } : t)));

    try {
      const sha = await sha256Hex(file);
      update({ phase: 'presigning' });

      const pres = await requestUrl.mutateAsync({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
      });

      update({ phase: 'uploading' });
      await putToR2({
        url: pres.upload.url,
        file,
        headers: pres.upload.headers,
        onProgress: (loaded, total) => update({ loaded, total }),
      });

      update({ phase: 'finalizing' });
      await finalize.mutateAsync({ id: pres.document.id, sha256: sha });

      update({ phase: 'done', loaded: file.size });
      notifications.show({
        title: 'Uploaded',
        message: file.name,
        color: 'pine',
      });
      // Remove the completed task card after a short delay
      setTimeout(() => {
        setTasks((prev) => prev.filter((t) => t.id !== localId));
      }, 2500);
    } catch (err) {
      const msg = err instanceof ApiError || err instanceof Error ? err.message : 'upload_failed';
      update({ phase: 'error', error: msg });
      notifications.show({
        title: 'Upload failed',
        message: `${file.name}: ${msg}`,
        color: 'red',
      });
    }
  }

  async function handleDownload(doc: Document) {
    try {
      const res = await requestDownload.mutateAsync(doc.id);
      // Hidden anchor click triggers the Save dialog with the original
      // filename instead of opening a new tab. R2 sets the Content-
      // Disposition attachment header on the presigned URL.
      const a = document.createElement('a');
      a.href = res.url;
      if (res.filename) a.download = res.filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      notifications.show({
        title: 'Download failed',
        message: err instanceof Error ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  function handleDelete(doc: Document) {
    modals.openConfirmModal({
      title: 'Delete document',
      children: (
        <Text>
          Remove <b>{doc.original_filename || doc.id.slice(0, 8)}</b>? The object stays in the
          bucket for now but will no longer appear in search or attachments.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await softDelete.mutateAsync(doc.id);
        notifications.show({ title: 'Deleted', message: doc.original_filename ?? 'document', color: 'pine' });
      },
    });
  }

  if (!projectId) {
    return (
      <Stack>
        <Title order={2}>Documents</Title>
        <Text c="dimmed">Pick a project to view its documents.</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Documents</Title>
          <Text c="dimmed" size="sm">
            PDFs and images attached to this project. Max {Math.round(MAX_SIZE / (1024 * 1024))} MB each.
          </Text>
        </Stack>
        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconFileUpload size={14} />}
            component={Link}
            to="/documents/ingest"
          >
            Bulk ingest
          </Button>
          <Button
            variant="default"
            leftSection={<IconHistory size={14} />}
            component={Link}
            to="/documents/activity"
          >
            Activity
          </Button>
          <TextInput
            placeholder="Search filename or title"
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            w={280}
          />
        </Group>
      </Group>

      {canWrite && (
        <Dropzone
          onDrop={handleDrop}
          onReject={(rejections) => {
            const first = rejections[0];
            notifications.show({
              title: 'Rejected',
              message:
                first?.errors[0]?.message ??
                'File type not allowed or too large',
              color: 'red',
            });
          }}
          accept={ACCEPT}
          maxSize={MAX_SIZE}
          multiple
        >
          <Group justify="center" gap="xl" mih={120} style={{ pointerEvents: 'none' }}>
            <Dropzone.Accept>
              <IconArrowUp size={40} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={40} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconFileUpload size={40} />
            </Dropzone.Idle>
            <Stack gap={4}>
              <Text size="md" fw={500}>
                Drop PDFs or images here, or click to browse
              </Text>
              <Text size="xs" c="dimmed">
                Accepted: PDF, JPG, PNG, TIFF, WebP. Max {Math.round(MAX_SIZE / (1024 * 1024))} MB per file.
              </Text>
            </Stack>
          </Group>
        </Dropzone>
      )}

      {activeTasks.length > 0 && (
        <Stack gap="xs">
          {activeTasks.map((t) => (
            <Card key={t.id} withBorder padding="sm">
              <Group justify="space-between">
                <Text size="sm" fw={500} lineClamp={1}>
                  {t.name}
                </Text>
                <Badge
                  variant="light"
                  color={t.phase === 'error' ? 'red' : 'blue'}
                  tt="lowercase"
                >
                  {t.phase === 'error' ? (t.error ?? 'error') : t.phase}
                </Badge>
              </Group>
              <Progress
                mt={6}
                value={
                  t.phase === 'uploading' && t.total > 0
                    ? (t.loaded / t.total) * 100
                    : t.phase === 'error'
                      ? 100
                      : 0
                }
                color={t.phase === 'error' ? 'red' : 'pine'}
                animated={t.phase === 'uploading'}
              />
            </Card>
          ))}
        </Stack>
      )}

      <DataTable
        withTableBorder
        highlightOnHover
        minHeight={150}
        fetching={isLoading}
        records={docs}
        idAccessor="id"
        rowStyle={(d) => (d.sha256 ? { cursor: 'pointer' } : { cursor: 'default' })}
        onRowClick={({ record }) => {
          if (record.sha256) setPreviewDoc(record);
        }}
        columns={[
          {
            accessor: 'original_filename',
            title: 'Filename',
            render: (d) => (
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {d.original_filename ?? <Text span c="dimmed">(no name)</Text>}
                </Text>
                {d.title && (
                  <Text size="xs" c="dimmed">
                    {d.title}
                  </Text>
                )}
              </Stack>
            ),
          },
          {
            accessor: 'content_type',
            title: 'Type',
            width: 140,
            render: (d) => <Code>{d.content_type ?? '—'}</Code>,
          },
          {
            accessor: 'file_size_bytes',
            title: 'Size',
            width: 100,
            render: (d) => (d.file_size_bytes ? humanSize(Number(d.file_size_bytes)) : '—'),
          },
          {
            accessor: 'sha256',
            title: 'Verified',
            width: 100,
            render: (d) =>
              d.sha256 ? (
                <Badge color="pine" variant="light">
                  sha256
                </Badge>
              ) : (
                <Badge color="gray" variant="light">
                  pending
                </Badge>
              ),
          },
          {
            accessor: 'uploaded_at',
            title: 'Uploaded',
            width: 140,
            render: (d) => (d.uploaded_at ? dayjs(d.uploaded_at).format('YYYY-MM-DD HH:mm') : '—'),
          },
          {
            accessor: 'actions',
            title: '',
            width: 90,
            textAlign: 'right',
            render: (d) => (
              <Group gap={4} justify="flex-end" onClick={(e) => e.stopPropagation()}>
                <Tooltip label="Download">
                  <ActionIcon
                    variant="subtle"
                    onClick={() => handleDownload(d)}
                    aria-label="Download"
                    disabled={!d.sha256}
                  >
                    <IconDownload size={16} />
                  </ActionIcon>
                </Tooltip>
                {canWrite && (
                  <Tooltip label="Delete">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => handleDelete(d)}
                      aria-label="Delete"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
      />

      <DocumentPreviewModal
        opened={previewDoc !== null}
        onClose={() => setPreviewDoc(null)}
        doc={previewDoc}
      />
    </Stack>
  );
}

